import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";

const DOCS = "/uploads/documents/";

// Cle localStorage : date de publication du dernier PV vu par ce navigateur.
// Utilisee par la nav pour afficher le badge "nouveau".
export const CM_SEEN_KEY = "mairie_cm_last_seen";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

export default function ConseilMunicipal() {
  const [items, setItems] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [annee, setAnnee] = useState("");
  const [recherche, setRecherche] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/conseils")
      .then((r) => {
        if (cancelled) return;
        const list = r.items || [];
        setItems(list);
        setAnnees(r.annees || []);
        if (list.length > 0) setSelected(list[0]);
        // Marquer comme "vu" : on enregistre la date de publication la plus recente
        const latest = list.reduce((acc, c) =>
          (!acc || c.date_publication > acc) ? c.date_publication : acc, null);
        if (latest) {
          try { localStorage.setItem(CM_SEEN_KEY, latest); } catch { /* ignore */ }
        }
      })
      .catch(() => { if (!cancelled) setError("Impossible de charger les conseils municipaux."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return items.filter((c) => {
      if (annee && c.date_conseil.slice(0, 4) !== annee) return false;
      if (q && !c.titre.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, annee, recherche]);

  return (
    <div className="container">
      <h1>Conseil municipal</h1>
      <p className="muted mb-3">
        Retrouvez les comptes rendus (procès-verbaux) des séances du conseil
        municipal de La Chapelle-Saint-Fray. Cliquez sur un PV pour le consulter.
      </p>

      {loading && <p className="muted">Chargement…</p>}
      {error && <div className="error">{error}</div>}

      {!loading && items.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "2rem 1rem" }}>
          <p className="muted" style={{ marginBottom: 0 }}>
            Aucun compte rendu n'est encore disponible.
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          {/* Filtres */}
          <div className="cm-filters">
            <input
              type="search"
              className="cm-search"
              placeholder="Rechercher un PV…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <select value={annee} onChange={(e) => setAnnee(e.target.value)}>
              <option value="">Toutes les années</option>
              {annees.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="cm-layout">
            {/* Liste */}
            <aside className="cm-list">
              <h3 style={{ marginTop: 0, fontSize: "1.05rem" }}>
                {filtered.length} compte{filtered.length > 1 ? "s" : ""} rendu{filtered.length > 1 ? "s" : ""}
              </h3>
              {filtered.length === 0 && (
                <p className="muted" style={{ fontSize: "0.9em" }}>
                  Aucun PV pour ces critères.
                </p>
              )}
              <div className="cm-list-items">
                {filtered.map((c) => {
                  const isActive = selected && c.id === selected.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelected(c)}
                      className={`cm-item ${isActive ? "active" : ""}`}>
                      <strong>{c.titre}</strong>
                      <span className="cm-item-meta">
                        Séance du {formatDate(c.date_conseil)}
                      </span>
                      <span className="cm-item-meta muted">
                        Publié le {formatDate(c.date_publication)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Visionneuse */}
            <div className="cm-viewer-wrap">
              {selected ? (
                <>
                  <div className="cm-viewer-head">
                    <div>
                      <h3 style={{ margin: 0 }}>{selected.titre}</h3>
                      <p className="muted" style={{ margin: "0.2rem 0 0", fontSize: "0.9em" }}>
                        Séance du {formatDate(selected.date_conseil)} ·
                        Publié le {formatDate(selected.date_publication)}
                      </p>
                    </div>
                    <a
                      href={DOCS + selected.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cm-newtab-btn">
                      ↗ Ouvrir dans un nouvel onglet
                    </a>
                  </div>
                  <iframe
                    key={selected.id}
                    title={selected.titre}
                    src={DOCS + selected.filename}
                    className="cm-iframe"
                  />
                  <p className="muted" style={{ fontSize: "0.8em", marginTop: "0.5rem" }}>
                    💡 Sur mobile, si le document ne s'affiche pas, utilisez le
                    bouton « Ouvrir dans un nouvel onglet ».
                  </p>
                </>
              ) : (
                <p className="muted">Sélectionnez un PV dans la liste.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
